import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["card", "dropzone"]

  connect() {
    // TicketDragController connected
    this.lastHighlightedDropzone = null

    this.cardTargets.forEach(card => {
      const isDraggable = card.getAttribute("draggable") === "true"
      card.setAttribute("draggable", isDraggable)
      // Remove existing listeners to avoid duplicates
      card.removeEventListener("dragstart", this.dragStart)
      card.removeEventListener("dragend", this.dragEnd)
      if (isDraggable) {
        card.addEventListener("dragstart", this.dragStart.bind(this))
        card.addEventListener("dragend", this.dragEnd.bind(this))
      }
    })

    this.dropzoneTargets.forEach(dropzone => {
      dropzone.addEventListener("dragover", this.allowDrop.bind(this))
      dropzone.addEventListener("drop", this.handleDrop.bind(this))
      dropzone.addEventListener("dragenter", this.highlightDropzone.bind(this), true)
      dropzone.addEventListener("dragleave", this.resetDropzone.bind(this), true)
    })
  }

  dragStart(event) {
    const card = event.target.closest("[data-ticket-drag-target='card']")
    if (card) {
      event.target.style.opacity = "0.5"
      event.dataTransfer.setData("text/plain", card.dataset.ticketId)
      event.dataTransfer.effectAllowed = "move"
      console.log("🚚 Iniciando drag de tarjeta", card.dataset.ticketId)
    }
  }

  allowDrop(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const { clientX, clientY } = event
    let closestZone = null
    let smallestDistance = Infinity

    this.dropzoneTargets.forEach(zone => {
      const rect = zone.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      const dx = centerX - clientX
      const dy = centerY - clientY
      const distance = dx * dx + dy * dy

      if (distance < smallestDistance) {
        smallestDistance = distance
        closestZone = zone
      }
    })

    if (closestZone && closestZone !== this.lastHighlightedDropzone) {
      if (this.lastHighlightedDropzone) {
        this.resetDropzoneVisual(this.lastHighlightedDropzone)
      }
      this.highlightDropzoneVisual(closestZone)
      this.lastHighlightedDropzone = closestZone
    }
  }

  highlightDropzone(event) {
    const dropzone = event.currentTarget
    dropzone.classList.add("bg-orange-50", "ring-2", "ring-orange-300", "transition", "duration-300")
    console.log("🎯 Drag enter sobre zona", dropzone.dataset.date)
  }

  resetDropzone(event) {
    const dropzone = event.currentTarget
    dropzone.classList.remove("bg-orange-50", "ring-2", "ring-orange-300", "transition", "duration-300")
    console.log("⬅️ Drag leave de zona", dropzone.dataset.date)
  }

  handleDrop(event) {
    event.preventDefault()
    if (this.lastHighlightedDropzone) {
      this.resetDropzoneVisual(this.lastHighlightedDropzone)
      this.lastHighlightedDropzone = null
    }
    const ticketId = event.dataTransfer.getData("text/plain")
    const dropzone = event.currentTarget
    const draggedCard = document.querySelector(`[data-ticket-id='${ticketId}']`)
    if (draggedCard && dropzone) {
      draggedCard.style.opacity = "1"
      const newDate = dropzone.dataset.date

      // Encuentra el contenedor padre original antes de mover la tarjeta
      const sourceDropzone = draggedCard.closest("[data-ticket-drag-target='dropzone']")

      fetch(`/apps/home-repairs/tickets/${ticketId}/schedule`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
        },
        body: JSON.stringify({ fecha: newDate === "null" ? null : newDate })
      })
      .then(response => {
        if (response.ok) {
          console.log("💾 Reprogramación guardada con éxito")

          // 🔄 Mover la tarjeta visualmente al nuevo contenedor
          let ticketGrid = dropzone.querySelector(".grid")
          if (ticketGrid) {
            ticketGrid.appendChild(draggedCard)
            console.log("🧩 Tarjeta reubicada en el DOM")

            const noTicketsMessage = dropzone.querySelector("p.text-xs.text-gray-400.italic")
            if (noTicketsMessage) {
              noTicketsMessage.remove()
            }

            // Limpiar elementos previos de contador o mensaje vacío
            dropzone.querySelectorAll("p.text-xs.text-gray-500, p.text-xs.text-gray-400.italic").forEach(el => el.remove())

            const counterParagraph = document.createElement("p")
            counterParagraph.className = "text-xs text-gray-500"

            const ticketCount = ticketGrid.querySelectorAll("[data-ticket-id]").length

            const pipelineCounts = {}
            ticketGrid.querySelectorAll("[data-pipeline]").forEach(card => {
              const pipeline = card.dataset.pipeline
              pipelineCounts[pipeline] = (pipelineCounts[pipeline] || 0) + 1
            })

            const resumen = Object.entries(pipelineCounts)
              .map(([pipeline, count]) => `${count} ${pipeline}`)
              .join(", ")

            counterParagraph.textContent = `${ticketCount} tickets (${resumen})`
            ticketGrid.insertAdjacentElement("beforebegin", counterParagraph)

            // 🧹 Actualizar contador en columna de origen
            if (sourceDropzone && sourceDropzone !== dropzone) {
              const sourceGrid = sourceDropzone.querySelector(".grid")
              // Limpiar elementos previos de contador o mensaje vacío en sourceDropzone
              sourceDropzone.querySelectorAll("p.text-xs.text-gray-500, p.text-xs.text-gray-400.italic").forEach(el => el.remove())
              const sourceCounter = document.createElement("p")
              sourceCounter.className = "text-xs text-gray-500"

              if (sourceGrid) {
                const newCount = sourceGrid.querySelectorAll("[data-ticket-id]").length
                const sourcePipelineCounts = {}
                sourceGrid.querySelectorAll("[data-pipeline]").forEach(card => {
                  const pipeline = card.dataset.pipeline
                  sourcePipelineCounts[pipeline] = (sourcePipelineCounts[pipeline] || 0) + 1
                })

                const resumen = Object.entries(sourcePipelineCounts)
                  .map(([pipeline, count]) => `${count} ${pipeline}`)
                  .join(", ")

                if (newCount > 0) {
                  sourceCounter.textContent = `${newCount} tickets (${resumen})`
                  sourceGrid.insertAdjacentElement("beforebegin", sourceCounter)
                } else {
                  sourceCounter.textContent = ""
                  sourceDropzone.appendChild(sourceCounter)

                  const sourceGridElement = sourceDropzone.querySelector("div.grid")
                  if (sourceGridElement) {
                    sourceGridElement.remove()
                  }

                  let noTicketsMessage = sourceDropzone.querySelector("p.text-xs.text-gray-400.italic")
                  if (!noTicketsMessage) {
                    noTicketsMessage = document.createElement("p")
                    noTicketsMessage.className = "text-xs text-gray-400 italic"
                    noTicketsMessage.textContent = "Sin tickets"
                    sourceDropzone.appendChild(noTicketsMessage)
                  }
                }
              }
            }
          } else {
            // Si no hay grid, crearlo (útil para columnas vacías inicialmente)
            // Limpiar elementos previos de contador o mensaje vacío
            dropzone.querySelectorAll("p.text-xs.text-gray-500, p.text-xs.text-gray-400.italic").forEach(el => el.remove())

            const newGrid = document.createElement("div")
            newGrid.className = "grid gap-4"
            dropzone.appendChild(newGrid)

            // Agregar contador
            const counterParagraph = document.createElement("p")
            counterParagraph.className = "text-xs text-gray-500"
            newGrid.insertAdjacentElement("beforebegin", counterParagraph)

            newGrid.appendChild(draggedCard)
            console.log("🆕 Grid creado y tarjeta agregada")
            ticketGrid = newGrid

            const ticketCount = ticketGrid.querySelectorAll("[data-ticket-id]").length
            const pipelineCounts = {}
            ticketGrid.querySelectorAll("[data-pipeline]").forEach(card => {
              const pipeline = card.dataset.pipeline
              pipelineCounts[pipeline] = (pipelineCounts[pipeline] || 0) + 1
            })

            const resumen = Object.entries(pipelineCounts)
              .map(([pipeline, count]) => `${count} ${pipeline}`)
              .join(", ")

            counterParagraph.textContent = `${ticketCount} tickets (${resumen})`
          }

          // Opcional: actualizar internamente su fecha si usas data-fecha o similar
          draggedCard.dataset.fecha = newDate
        } else {
          console.error("❌ Error al reprogramar ticket")
        }
      })
      .catch(error => {
        console.error("⚠️ Error de red", error)
      })
    }
  }

  highlightDropzoneVisual(dropzone) {
    dropzone.classList.add("bg-orange-50", "ring-2", "ring-orange-300", "transition", "duration-300")
  }

  resetDropzoneVisual(dropzone) {
    dropzone.classList.remove("bg-orange-50", "ring-2", "ring-orange-300", "transition", "duration-300")
  }
  dragEnd(event) {
    event.target.style.opacity = "1"
  }
}